import { OpHTMLElement } from '../flow/declarations';
import { price, subscriptionPeriodTranslation, url } from './Helpers';
import Image from './Image';
import PictureMedia from './PictureMedia';
import ProductStatusClasses from '../constants/ProductStatusClasses';
import AsyncRequest from './AsyncRequest';
import Icon from './Icon';

export function buildCartItem(element: Node, data: Object): HTMLElement {
  const item: OpHTMLElement = (element: any);
  item.dataset.sku = data.sku;
  item.dataset.amount = data.amount;
  if (data.subscription) {
    item.dataset.period = data.subscription.period;
  }

  const amount: ?HTMLElement = item.getElementByRef('amount');
  if (amount) {
    amount.innerText = data.amount;
  }

  const link: ?HTMLElement = (item instanceof HTMLAnchorElement) ? item : item.getElementByRef('link');
  if (link) {
    if (isClickable(data)) {
      link.setAttribute('href', url(`/${data.slug}?sku=${data.sku}`));
    } else {
      const imageWrapper: HTMLElement = document.createElement('div');
      imageWrapper.classList.add(...link.classList);
      imageWrapper.innerHTML = link.innerHTML;
      if (link.parentElement) {
        link.parentElement.insertBefore(imageWrapper, link);
      }
      link.remove();
    }
  }

  const imageElement: ?HTMLElement = item.getElementByRef('picture');
  if (imageElement) {
    const image: Image = window.store.cdn.image(data.image?.src, data.image?.alt);
    imageElement.innerHTML = new PictureMedia(imageElement, image).build().innerHTML;
  }

  const title: ?HTMLElement = item.getElementByRef('title');
  if (title) {
    if (isClickable(data)) {
      title.innerText = data.title;

      if (title instanceof HTMLAnchorElement) {
        title.setAttribute('href', url(`/${data.slug}?sku=${data.sku}`));
      }
    } else {
      const titleWithoutLink: HTMLElement = document.createElement('div');
      titleWithoutLink.classList.add(...title.classList);
      titleWithoutLink.innerText = data.title;
      title.parentElement.insertBefore(titleWithoutLink, title);
      title.remove();
    }
  }

  const status: ?HTMLElement = item.getElementByRef('status');
  if (status) {
    status.innerHTML = '';
    Object.values(ProductStatusClasses).forEach((statusClass: any) => {
      status.classList.remove(statusClass);
    });
  }
  const originalTotal: ?HTMLElement = item.getElementByRef('total-original');
  if (originalTotal && data.subscription) {
    originalTotal.classList.remove('hidden');
    originalTotal.innerText = price(data.price_original * data.amount);
  }
  if (!data.subscription?.period) {
    if (originalTotal) {
      originalTotal.remove();
    }
  }
  const total: ?HTMLElement = item.getElementByRef('total');
  if (total) {
    if (!data.subscription?.frequency) {
      total.innerText = data.total > 0 ? price(data.total) : window.store.getTranslation('Free');
    } else {
      total.innerText = price(data.amount * data.price);
      total.classList.add('text-secondary');
    }

    if (!data.subscription?.period) {
      total.classList.remove('text-secondary');
    }
  }

  const counter: ?HTMLElement = item.getElementByRef('counter');
  if (counter) {
    if (isUpdatable(data)) {
      counter.dataset.sku = data.sku;
      counter.dataset.name = data.title;
      counter.dataset.subscriptionPeriod = data.subscription?.period;

      const opCounter: OpHTMLElement = (counter: any);
      const amount: ?HTMLInputElement = opCounter.getElementByRef('amount');
      if (amount) {
        amount.setAttribute('value', data.amount);
        const minus = opCounter.getElementByRef('minus');
        if (!minus) return;
        const minusIcon = minus.getElementByRef('icon');
        if (!minusIcon) return;
        if (amount.value === '1') {
          minusIcon.setAttribute('href', '#icon-garbage-can');
        } else {
          minusIcon.setAttribute('href', '#icon-minus');
        }
      }
    } else {
      counter.remove();
    }
  }

  const subscriptionLabel = item.getElementByRef('subscription-label');
  if (subscriptionLabel) {
    if (!data.subscription?.period) {
      subscriptionLabel.remove();
    } else {
      const subscriptionPeriod = item.getElementByRef('subscription-period');
      if (subscriptionPeriod && data.subscription.period) {
        subscriptionPeriod.innerHTML = subscriptionPeriodTranslation(data.subscription.period, data.subscription.frequency.id);
      }
    }
  }

  const subscriptionPeriods = item.getElementByRef('subscription-periods');
  if (subscriptionPeriods) {
    if (data.is_free || !data.subscription) {
      subscriptionPeriods.parentElement.remove();
    } else {
      Array.from(subscriptionPeriods.options).forEach((option: HTMLElement) => {
        if (+option.value === data.subscription?.period) {
          option.selected = true;
        }
      });

      subscriptionPeriods.dataset.sku = data.sku;
    }
  }

  const remover: ?HTMLElement = item.getElementByRef('remover');
  if (remover) {
    if (!data.is_free) {
      remover.dataset.sku = data.sku;
      remover.dataset.amount = data.amount;
      remover.dataset.name = data.title;
      remover.dataset.subscriptionPeriod = data.subscription?.period;
    } else {
      remover.remove();
    }
  }

  return item;
}

function isUpdatable(data: Object): boolean {
  return !(data.hasOwnProperty('is_free') && data.is_free === true);
}

function isClickable(data: Object): boolean {
  return !(data.hasOwnProperty('is_free') && data.is_free === true);
}

export function updateStatuses(products: Array<Object>, children: HTMLElement): void {
  AsyncRequest.post('', {
    products
  }, '/api/product-statuses')
    .then(({ data }: Object) => {
      Array.from(children).forEach((child: HTMLElement, index: number) => {
        const opChild: OpHTMLElement = (child: any);
        const statusElement: ?HTMLElement = opChild.getElementByRef('status');
        const statusObject: Object = data[index];
        if (statusElement) {
          statusElement.innerHTML = `
              ${new Icon({ icon: statusObject.icon, width: 12 }).generate().outerHTML}
              ${statusObject.text}
            `;

          statusElement.classList.add(statusObject.class);
        }
      });
    })
    .catch(() => {
      console.warn(`${this.name}: Could not retrieve the product statuses`);
    });
}
